import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import type { IconType } from "react-icons";
import {
  MdDashboard,
  MdHotel,
  MdPeople,
  MdCelebration,
  MdDescription,
  MdRoomService,
  MdCleaningServices,
  MdRestaurant,
  MdSettings,
  MdBuild,
  MdAccountBalance,
  MdKingBed,
  MdTheaterComedy,
  MdPerson,
  MdLock,
  MdApartment,
  MdSpa,
  MdDomain,
  MdLayers,
  MdMiscellaneousServices,
  MdCategory,
  MdLabel,
  MdMenuBook,
  MdRestaurantMenu,
  MdFreeBreakfast,
  MdRamenDining,
  MdReceiptLong,
  MdPrint,
  MdReceipt,
  MdLocalOffer,
  MdSync,
  MdSupportAgent,
  MdChecklist,
  MdSoupKitchen,
  MdTableRestaurant,
  MdHowToReg,
  MdLogout,
  MdMenu,
  MdMenuOpen,
  MdExpandMore,
  MdChevronRight,
} from "react-icons/md";
import { useAuth } from "../contexts/AuthContext";
import { usePermissions } from "../contexts/PermissionsContext";
import { OTA_ATTENTION_EVENT, getOtaAttention } from "../utils/channelManagerApi";
import { getViewingTenant } from "../utils/partnerViewContext";
import {
  getRoomServiceNewCount,
  getRoomServiceUrgentCount,
  ROOM_SERVICE_ALERT_EVENT,
} from "../utils/roomServiceAlert";
import {
  getRestaurantNewOrderCount,
  RESTAURANT_ORDER_ALERT_EVENT,
} from "../utils/restaurantOrderAlert";

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

interface NavItem {
  name: string;
  href: string;
  icon: IconType;
  /** Matches MenuMaster.menuName — gates visibility to the logged-in
   * role's permission grid (see PermissionsContext). Compared
   * case-insensitively. */
  menuName: string;
  /** Sub-heading the item is listed under inside its group (Settings). Items
   * of one section must be adjacent in the children list. */
  section?: string;
}

interface NavGroup {
  name: string;
  icon: IconType;
  href?: string;
  children: NavItem[];
}

const mainNavigation: NavItem[] = [
  { name: "Dashboard", href: "/admin", icon: MdDashboard, menuName: "dashboard" },
  { name: "Bookings (Rooms)", href: "/admin/bookings", icon: MdHotel, menuName: "bookings" },
  { name: "Customers", href: "/admin/customers", icon: MdPeople, menuName: "customers" },
  { name: "Events", href: "/admin/events", icon: MdCelebration, menuName: "events" },
  // Room and event tax invoices with their GST e-invoice (IRN) state. Shares the
  // Bookings permission row: no menumaster row of its own is needed.
  { name: "Tax Invoices", href: "/admin/tax-invoices", icon: MdReceipt, menuName: "bookings" },
  { name: "Quotations", href: "/admin/quotations", icon: MdDescription, menuName: "quotations" },
  { name: "Room Service", href: "/admin/room-service", icon: MdRoomService, menuName: "roomService" },
  // Room turnover, not guest requests. The queue above is what a guest ASKED
  // for while in the room; this is what the hotel owes the room after they
  // have gone -- and the only one of the two that decides whether the room can
  // be sold again. Its menumaster row is seeded by
  // API/sql/2026-09-16_create_housekeeping.sql; without it a role-gated user
  // sees no link.
  { name: "Housekeeping", href: "/admin/housekeeping", icon: MdCleaningServices, menuName: "housekeeping" },
];

/** Restaurant and the two masters that configure it.
 *
 * A group rather than a flat link because Kitchens and Kitchen Categories only
 * mean anything to the room-restaurant menu — a kitchen cooks menu items and
 * nothing else in this product — so filing them under Settings put them three
 * screens away from the only thing they affect.
 *
 * "Orders & Menu" keeps menuName "restaurant": it is the same page and the
 * same permission row the flat link used, so no role's access changes just
 * because the nav moved. Menu items are still added from that page's Menu tab;
 * the two screens below it configure what a menu item can be filed under. */
const restaurantGroup: NavGroup = {
  name: "Room Restaurant",
  icon: MdRestaurant,
  href: "/admin/restaurant",
  children: [
    { name: "Orders", href: "/admin/restaurant", icon: MdTableRestaurant, menuName: "restaurant" },
    // The dish catalogue, split out of the Orders page; same permission row.
    { name: "Menu", href: "/admin/restaurant-menu", icon: MdRestaurantMenu, menuName: "restaurant" },
    // The kitchen board; same permission row as Orders & Menu.
    { name: "KOT", href: "/admin/kot", icon: MdReceiptLong, menuName: "restaurant" },
    // The food hall's +1 Guest counter. Its menumaster row and grants (copied
    // from "restaurant") come from API/sql/2026-10-13_create_foodhallentry.sql.
    { name: "Food Hall Entry", href: "/admin/food-hall", icon: MdHowToReg, menuName: "foodHall" },
    { name: "Kitchens", href: "/admin/kitchens", icon: MdSoupKitchen, menuName: "kitchens" },
    // Kitchen Types and Kitchen Categories are hidden: categories are made on
    // the Menu page and a kitchen's type is picked on the kitchen itself. Both
    // pages and routes stay.
  ],
};

const settingsGroup: NavGroup = {
  name: "Settings",
  icon: MdSettings,
  href: "/admin/settings",
  // Grouped under sub-headings so the long list is scannable. A section
  // whose items the role can't see simply doesn't appear.
  children: [
    { section: "General", name: "General", href: "/admin/settings", icon: MdBuild, menuName: "general" },
    { section: "General", name: "Branches", href: "/admin/branches", icon: MdApartment, menuName: "branches" },
    { section: "General", name: "Status Masters", href: "/admin/status-masters", icon: MdCategory, menuName: "statusMasters" },
    { section: "General", name: "Tax & Service", href: "/admin/tax-service-settings", icon: MdReceiptLong, menuName: "taxServiceSettings" },
    // Per-PC printer choice (KOT thermal / A4); same access as General.
    { section: "General", name: "Printers", href: "/admin/printers", icon: MdPrint, menuName: "general" },

    { section: "Users & Roles", name: "Users", href: "/admin/users", icon: MdPerson, menuName: "users" },
    { section: "Users & Roles", name: "Roles", href: "/admin/roles", icon: MdLock, menuName: "roles" },

    { section: "Amenities & Facilities", name: "Amenities", href: "/admin/amenities", icon: MdSpa, menuName: "amenities" },
    { section: "Amenities & Facilities", name: "Facilities", href: "/admin/facilities", icon: MdMiscellaneousServices, menuName: "facilities" },

    { section: "Property", name: "Buildings", href: "/admin/buildings", icon: MdDomain, menuName: "buildings" },
    { section: "Property", name: "Floors", href: "/admin/floors", icon: MdLayers, menuName: "floors" },
    { section: "Property", name: "Halls", href: "/admin/halls", icon: MdAccountBalance, menuName: "halls" },

    { section: "Rooms", name: "Room Meal Types", href: "/admin/meal-types", icon: MdFreeBreakfast, menuName: "mealTypes" },
    { section: "Rooms", name: "Room Meal Plans", href: "/admin/meal-plans", icon: MdRamenDining, menuName: "mealPlans" },
    { section: "Rooms", name: "Room Rate Plans", href: "/admin/rate-plans", icon: MdLocalOffer, menuName: "ratePlans" },
    { section: "Rooms", name: "Rooms", href: "/admin/rooms", icon: MdKingBed, menuName: "rooms" },

    // Aiosell connection and OTA mapping. Seeded, with Rate Plans' role
    // permissions, by sql/2026-09-24_create_channel_manager.sql.
    { section: "OTA Booking", name: "OTA Booking", href: "/admin/channel-manager", icon: MdSync, menuName: "channelManager" },

    // Food Categories is CATERING's master and stays here. The room-restaurant
    // menu's own chain — Kitchens and Kitchen Categories — lives under
    // Restaurant, beside the menu it configures.
    { section: "Events & Catering", name: "Event Types", href: "/admin/event-types", icon: MdTheaterComedy, menuName: "eventTypes" },
    // Packages (hotel deals) is hidden for this phase; its page and route stay.
    { section: "Events & Catering", name: "Food Categories", href: "/admin/food-categories", icon: MdLabel, menuName: "foodCategories" },
    { section: "Events & Catering", name: "Food Items", href: "/admin/food-menus", icon: MdMenuBook, menuName: "foodMenus" },
    { section: "Events & Catering", name: "Food Packages", href: "/admin/food-packages", icon: MdRestaurantMenu, menuName: "foodPackages" },
    { section: "Events & Catering", name: "Additional Services", href: "/admin/additional-services", icon: MdRoomService, menuName: "additionalServices" },

    { section: "Guest Services", name: "Guest Service Categories", href: "/admin/guest-service-categories", icon: MdSupportAgent, menuName: "guestServiceCategories" },
    { section: "Guest Services", name: "Guest Service Items", href: "/admin/guest-service-items", icon: MdChecklist, menuName: "guestServiceItems" },
  ],
};

/** Whether a nav href covers the current location.
 *
 * "/admin" and "/admin/settings" are EXACT: both are prefixes of half the app,
 * so a startsWith would light them up on every page. Everything else matches by
 * prefix so a detail route keeps its parent highlighted.
 */
const matchesPath = (pathname: string, href: string) =>
  href === "/admin" || href === "/admin/settings"
    ? pathname === href
    : pathname.startsWith(href);

const isInGroup = (pathname: string, group: NavGroup) =>
  group.children.some((child) => matchesPath(pathname, child.href));

/** A count on a collapsible section's header, and on the child link it
 * belongs to when there is one. Urgent pulses (a new kitchen order); otherwise
 * it is a quiet amber count (OTA bookings waiting for the front desk). */
interface GroupAlert {
  count: number;
  title: string;
  urgent: boolean;
  href?: string;
}

const NO_ALERT: GroupAlert = { count: 0, title: "", urgent: false };

// How often the OTA attention badge is refreshed. Those bookings arrive from
// Aiosell, not from this browser, so nothing here would announce them.
const OTA_ATTENTION_REFRESH_MS = 60_000;

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggle }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [viewingTenant] = useState(() => getViewingTenant());

  // Preview flag takes priority (Control Center); otherwise show this real
  // user's own account name; "ICH"/"Management" is the last-resort generic
  // fallback (SuperAdmin's own view, or the account-name fetch failed).
  const brandName = viewingTenant?.tenantName ?? user?.accountName ?? "ICH";
  const brandSubtitle = viewingTenant || user?.accountName ? "Hotel Admin" : "Management";

  const isSettingsSection = isInGroup(location.pathname, settingsGroup);
  const isRestaurantSection = isInGroup(location.pathname, restaurantGroup);

  const [settingsOpen, setSettingsOpen] = useState(isSettingsSection);
  const [restaurantOpen, setRestaurantOpen] = useState(isRestaurantSection);

  // Settings sub-sections (General, Users & Roles, ...) start collapsed; the
  // one holding the current page is opened so the active link stays visible.
  const activeSettingsSection = settingsGroup.children.find((child) =>
    matchesPath(location.pathname, child.href)
  )?.section;
  const [openSections, setOpenSections] = useState<Set<string>>(
    () => new Set(activeSettingsSection ? [activeSettingsSection] : [])
  );
  useEffect(() => {
    if (!activeSettingsSection) return;
    setOpenSections((prev) =>
      prev.has(activeSettingsSection)
        ? prev
        : new Set(prev).add(activeSettingsSection)
    );
  }, [activeSettingsSection]);
  const toggleSection = (section: string) =>
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  const [roomServiceNewCount, setRoomServiceNewCount] = useState(
    getRoomServiceNewCount,
  );
  const [roomServiceUrgentCount, setRoomServiceUrgentCount] = useState(
    getRoomServiceUrgentCount,
  );
  const [restaurantNewOrderCount, setRestaurantNewOrderCount] = useState(
    getRestaurantNewOrderCount,
  );

  // Unrestricted for a SuperAdmin or a Control Center preview identity; for
  // everyone else only menus the role grants View on. Nothing is listed while
  // the first read is in flight, so the full list never flashes up and then
  // shrinks. Refreshed in the background, so a role change shows up without
  // a re-login.
  const { loading: menusLoading, can } = usePermissions();
  const isMenuVisible = (menuName: string) => can(menuName, "view");

  const visibleMainNavigation = menusLoading
    ? []
    : mainNavigation.filter((item) => isMenuVisible(item.menuName));
  const visibleSettingsChildren = menusLoading
    ? []
    : settingsGroup.children.filter((item) => isMenuVisible(item.menuName));
  const visibleRestaurantChildren = menusLoading
    ? []
    : restaurantGroup.children.filter((item) => isMenuVisible(item.menuName));

  // OTA bookings needing attention, across this user's own branches. Only
  // asked for when the role can see Channel Manager.
  const canSeeChannelManager = !menusLoading && isMenuVisible("channelManager");
  const [otaAttentionCount, setOtaAttentionCount] = useState(0);
  // The hotel being viewed (also when a super admin opens a partner's admin).
  const viewedAccountId = Number(user?.accountId) || undefined;
  useEffect(() => {
    if (!canSeeChannelManager) {
      setOtaAttentionCount(0);
      return;
    }
    let cancelled = false;
    const load = () => {
      getOtaAttention(viewedAccountId)
        .then((attention) => {
          if (!cancelled) setOtaAttentionCount(attention.count);
        })
        .catch(() => {
          // Keep the last count: the badge is a hint, and the page itself
          // lists every booking that needs attention.
        });
    };
    load();
    const timer = window.setInterval(load, OTA_ATTENTION_REFRESH_MS);
    window.addEventListener(OTA_ATTENTION_EVENT, load);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener(OTA_ATTENTION_EVENT, load);
    };
  }, [canSeeChannelManager, viewedAccountId]);

  useEffect(() => {
    if (isSettingsSection) setSettingsOpen(true);
  }, [isSettingsSection]);

  useEffect(() => {
    if (isRestaurantSection) setRestaurantOpen(true);
  }, [isRestaurantSection]);

  useEffect(() => {
    const syncAlert = (event?: Event) => {
      const detail = (event as CustomEvent | undefined)?.detail as
        | { newCount?: number; urgentCount?: number }
        | undefined;
      setRoomServiceNewCount(
        typeof detail?.newCount === "number"
          ? detail.newCount
          : getRoomServiceNewCount(),
      );
      setRoomServiceUrgentCount(
        typeof detail?.urgentCount === "number"
          ? detail.urgentCount
          : getRoomServiceUrgentCount(),
      );
    };

    window.addEventListener(ROOM_SERVICE_ALERT_EVENT, syncAlert);
    window.addEventListener("storage", syncAlert);
    syncAlert();
    return () => {
      window.removeEventListener(ROOM_SERVICE_ALERT_EVENT, syncAlert);
      window.removeEventListener("storage", syncAlert);
    };
  }, []);

  useEffect(() => {
    const syncRestaurantAlert = (event?: Event) => {
      const detail = (event as CustomEvent | undefined)?.detail as
        | { newCount?: number }
        | undefined;
      setRestaurantNewOrderCount(
        typeof detail?.newCount === "number"
          ? detail.newCount
          : getRestaurantNewOrderCount(),
      );
    };

    window.addEventListener(RESTAURANT_ORDER_ALERT_EVENT, syncRestaurantAlert);
    window.addEventListener("storage", syncRestaurantAlert);
    syncRestaurantAlert();
    return () => {
      window.removeEventListener(
        RESTAURANT_ORDER_ALERT_EVENT,
        syncRestaurantAlert,
      );
      window.removeEventListener("storage", syncRestaurantAlert);
    };
  }, []);

  const isActive = (href: string) => matchesPath(location.pathname, href);

  const linkClass = (href: string, nested = false) =>
    `relative flex items-center ${
      nested ? "px-3 py-1.5 ml-2" : "px-3 py-2"
    } rounded-xl transition-colors text-sm ${
      isActive(href)
        ? "bg-primary-50 text-primary-700 font-semibold dark:bg-primary-500/20 dark:text-primary-300"
        : "text-slate-500 hover:bg-gray-50 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
    }`;

  const ActiveBar: React.FC<{ show: boolean }> = ({ show }) =>
    show ? (
      <span className="absolute right-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-l-full bg-primary-600" />
    ) : null;

  /** One collapsible section — Restaurant, Settings.
   *
   * A render FUNCTION rather than a component: declaring a component inside
   * this one would give it a new identity every render, remounting the whole
   * subtree and losing focus and scroll on each keystroke elsewhere in the
   * sidebar.
   *
   * `alert` exists because Restaurant carries the new-order badge that used
   * to sit on its flat link, and Settings the OTA bookings needing attention
   * (Channel Manager). It renders on the HEADER, so the signal survives both
   * collapsing the sidebar and collapsing the group — a kitchen alert that
   * only appears when a section happens to be expanded is not an alert. When
   * it belongs to one child (`alert.href`), that child shows it too.
   */
  const renderGroup = (
    group: NavGroup,
    children: NavItem[],
    isSection: boolean,
    open: boolean,
    toggle: () => void,
    alert: GroupAlert = NO_ALERT,
  ) => {
    if (children.length === 0) return null;

    const alertCount = alert.count;
    const showAlert = alertCount > 0;
    const pillClass = alert.urgent
      ? "bg-rose-500 text-white animate-pulse"
      : "bg-amber-400 text-slate-900";
    const headerClass = `relative flex items-center px-3 py-2 rounded-xl transition-colors text-sm ${
      isSection
        ? "bg-primary-50 text-primary-700 font-semibold dark:bg-primary-500/20 dark:text-primary-300"
        : "text-slate-500 hover:bg-gray-50 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
    }`;

    return (
      <li key={group.name}>
        {isCollapsed ? (
          // Collapsed: the header is a plain link to the section's landing
          // page. There is no room for a disclosure triangle, and a click that
          // expanded an invisible list would do nothing useful.
          <Link
            to={group.href || children[0].href}
            className={`${headerClass} ${
              showAlert && !isSection ? "ring-1 ring-amber-400/60" : ""
            }`}
            title={showAlert ? alert.title : group.name}
          >
            <ActiveBar show={isSection} />
            <span className="relative text-lg mr-2 flex-shrink-0">
              <group.icon />
              {showAlert && (
                <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                  {alert.urgent && (
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                  )}
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-400" />
                </span>
              )}
            </span>
          </Link>
        ) : (
          <>
            <button
              type="button"
              onClick={toggle}
              className={`${headerClass} w-full justify-between ${
                showAlert && !isSection ? "ring-1 ring-amber-400/60" : ""
              }`}
            >
              <ActiveBar show={isSection} />
              <span className="flex items-center min-w-0">
                <span className="text-lg mr-2 flex-shrink-0">
                  <group.icon />
                </span>
                <span className="truncate">{group.name}</span>
              </span>
              <span className="flex items-center">
                {showAlert && (
                  <span
                    title={alert.title}
                    className={`mr-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-bold tabular-nums ${pillClass}`}
                  >
                    {alertCount}
                  </span>
                )}
                <span className="text-xs text-slate-400 dark:text-slate-500">
                  {open ? <MdExpandMore /> : <MdChevronRight />}
                </span>
              </span>
            </button>

            {open && (
              <ul className="mt-1 space-y-1 border-l border-gray-100 ml-4 pl-1">
                {children.map((child, index) => {
                  const section = child.section;
                  const startsSection =
                    !!section && section !== children[index - 1]?.section;
                  const sectionOpen = !section || openSections.has(section);
                  // A collapsed section still flags an alert on one of its
                  // pages (e.g. Channel Manager's pending mappings).
                  const sectionHasAlert =
                    !!section &&
                    showAlert &&
                    children.some(
                      (c) => c.section === section && c.href === alert.href
                    );
                  return (
                  <li key={child.name}>
                    {startsSection && (
                      <button
                        type="button"
                        onClick={() => toggleSection(section)}
                        aria-expanded={sectionOpen}
                        className={`w-full flex items-center justify-between px-3 pb-1 text-[11.5px] font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors ${
                          index === 0 ? "pt-1" : "pt-3"
                        }`}
                      >
                        <span className="flex items-center gap-1.5 truncate">
                          {section}
                          {!sectionOpen && sectionHasAlert && (
                            <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                          )}
                        </span>
                        <span className="text-xs">
                          {sectionOpen ? <MdExpandMore /> : <MdChevronRight />}
                        </span>
                      </button>
                    )}
                    {sectionOpen && (
                    <Link
                      to={child.href}
                      className={linkClass(child.href, true)}
                      title={showAlert && alert.href === child.href ? alert.title : undefined}
                    >
                      <span className="text-base mr-2 flex-shrink-0">
                        <child.icon />
                      </span>
                      <span className="truncate flex-1">{child.name}</span>
                      {showAlert && alert.href === child.href && (
                        <span
                          className={`ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-bold tabular-nums ${pillClass}`}
                        >
                          {alertCount}
                        </span>
                      )}
                    </Link>
                    )}
                  </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </li>
    );
  };

  return (
    <div
      className={`bg-white text-slate-700 dark:text-slate-300 border-r border-gray-100 transition-all duration-300 fixed left-0 top-0 h-full z-50 ${
        isCollapsed ? "w-16" : "w-56"
      } flex flex-col`}
    >
      {/* Logo Section. The collapse toggle sits beside the brand rather than
          as a full-width arrow bar under it: a lone back-arrow read as "go back
          a page", not "collapse the menu". */}
      <div className="p-3 border-b border-gray-100 flex-shrink-0">
        {isCollapsed ? (
          <div className="flex flex-col items-center gap-2">
            {viewingTenant?.logoUrl ? (
              <img
                src={viewingTenant.logoUrl}
                alt={`${viewingTenant.tenantName} logo`}
                className="w-10 h-10 rounded-xl object-cover shadow-soft"
              />
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-navy-800 to-primary-600 rounded-xl flex items-center justify-center shadow-soft">
                <span className="text-lg font-bold text-white">
                  {brandName.charAt(0).toUpperCase() || "?"}
                </span>
              </div>
            )}
            <button
              type="button"
              onClick={onToggle}
              title="Expand menu"
              aria-label="Expand menu"
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-gray-100 hover:text-slate-700 dark:text-slate-500 dark:hover:text-white transition-colors text-lg"
            >
              <MdMenu />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 w-full">
            {viewingTenant?.logoUrl ? (
              <img
                src={viewingTenant.logoUrl}
                alt={`${viewingTenant.tenantName} logo`}
                className="w-10 h-10 rounded-xl object-cover shadow-soft flex-shrink-0"
              />
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-navy-800 to-primary-600 rounded-xl flex items-center justify-center shadow-soft flex-shrink-0">
                <span className="text-lg font-bold text-white">
                  {brandName.charAt(0).toUpperCase() || "?"}
                </span>
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h1 className="text-lg font-bold text-slate-900 dark:text-white leading-tight truncate">
                {brandName}
              </h1>
              <p className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 tracking-widest uppercase truncate">
                {brandSubtitle}
              </p>
            </div>
            <button
              type="button"
              onClick={onToggle}
              title="Collapse menu"
              aria-label="Collapse menu"
              className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-lg text-slate-400 hover:bg-gray-100 hover:text-slate-700 dark:text-slate-500 dark:hover:text-white transition-colors text-lg"
            >
              <MdMenuOpen />
            </button>
          </div>
        )}
      </div>

      <nav className="flex-1 px-2 overflow-y-auto min-h-0">
        <ul className="space-y-1">
          {visibleMainNavigation.map((item) => {
            // Restaurant's own badge moved with it into restaurantGroup — see
            // renderGroup's alertCount. Room service is the only flat item
            // left that carries one.
            const isRoomService = item.href === "/admin/room-service";
            const alertCount = isRoomService ? roomServiceNewCount : 0;
            const alertIsUrgent = roomServiceUrgentCount > 0;
            const alertNoun = "new room service request";
            const showAlert = alertCount > 0;
            return (
              <li key={item.name}>
                <Link
                  to={item.href}
                  className={`${linkClass(item.href)} ${
                    showAlert && !isActive(item.href)
                      ? "ring-1 ring-amber-400/60"
                      : ""
                  }`}
                  title={
                    showAlert
                      ? `${alertCount} ${alertNoun}${alertCount === 1 ? "" : "s"}`
                      : item.name
                  }
                >
                  <ActiveBar show={isActive(item.href)} />
                  <span className="relative text-lg mr-2 flex-shrink-0">
                    <item.icon />
                    {showAlert && isCollapsed && (
                      <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-400" />
                      </span>
                    )}
                  </span>
                  {!isCollapsed && (
                    <>
                      <span className="truncate flex-1">{item.name}</span>
                      {showAlert && (
                        <span
                          className={`ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-bold tabular-nums ${
                            alertIsUrgent
                              ? "bg-rose-500 text-white animate-pulse"
                              : "bg-amber-400 text-slate-900"
                          }`}
                        >
                          {alertCount}
                        </span>
                      )}
                    </>
                  )}
                </Link>
              </li>
            );
          })}
          {renderGroup(
            restaurantGroup,
            visibleRestaurantChildren,
            isRestaurantSection,
            restaurantOpen,
            () => setRestaurantOpen((open) => !open),
            {
              count: restaurantNewOrderCount,
              title: `${restaurantNewOrderCount} new restaurant order${
                restaurantNewOrderCount === 1 ? "" : "s"
              }`,
              urgent: true,
            },
          )}
          {renderGroup(
            settingsGroup,
            visibleSettingsChildren,
            isSettingsSection,
            settingsOpen,
            () => setSettingsOpen((open) => !open),
            {
              count: otaAttentionCount,
              title: `${otaAttentionCount} OTA booking${
                otaAttentionCount === 1 ? " needs" : "s need"
              } attention`,
              urgent: false,
              href: "/admin/channel-manager",
            },
          )}
        </ul>
      </nav>

      {/* User Info Section at Bottom */}
      <div className="p-2 border-t border-gray-100 flex-shrink-0">
        {!isCollapsed ? (
          <div className="space-y-2">
            <div className="flex items-center space-x-2 p-2 rounded-xl bg-gradient-to-r from-navy-800 to-primary-600 shadow-soft">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                {user?.username?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-white truncate">
                  {user?.username}
                </p>
                <p className="text-[11px] text-primary-100 truncate">
                  {user?.role}
                </p>
              </div>
            </div>
            <button
              onClick={logout}
              className="w-full px-3 py-2 text-xs font-medium text-slate-500 dark:text-slate-400 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
            >
              Logout
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center space-y-2">
            <div className="w-8 h-8 bg-gradient-to-br from-navy-800 to-primary-600 rounded-full flex items-center justify-center text-white font-semibold shadow-soft text-sm">
              {user?.username?.charAt(0).toUpperCase()}
            </div>
            <button
              onClick={logout}
              className="w-8 h-8 text-slate-500 dark:text-slate-400 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors flex items-center justify-center text-sm"
              title="Logout"
            >
              <MdLogout />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
